import { useEffect, useState } from 'react';
import Grid from '@mui/material/Unstable_Grid2';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Box, Button, Typography } from '@mui/material';
import Loading from '../mui/Backdrop';
import dayjs, { Dayjs } from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { readPoolExpectAmtUsdt, readPoolRewardSettingList } from '@/api/asset/pool/read';
import {
  DatePickerType,
  CreatePoolRewardType,
  ResReadPoolRewardListType,
  UpdatePoolRewardType,
  RESULT_BIG,
} from '@/types';
import { DEFAULT_FORMAT, SUCCESS } from '@/lib/common/name-space';
import { createPoolReward } from '@/api/asset/pool/create';
import { toast } from 'react-toastify';
import { deleteReward, updatePoolReward } from '@/api/asset/pool/update';
import { rewardStateFilter } from '@/lib/filter/stakingRewardState';
import { compareDate, now } from '@/lib/dayjs';
import SendAlert from '../alert/SendAlert';
import { packBigNumbers } from '@/lib/BigNumbers';
import { promiseAllSettled } from '@/lib/Promise';

export default function PoolRewardDetailSetting({ pool_id }: { pool_id: string }) {
  const [pools, setPools] = useState<DatePickerType[]>([
    { id: 0, startDt: null, endDt: null, ticker: '0', state: 0, totExpectAmt: '0', totExpectUsdt: '0' },
  ]);
  const [oldPoolLength, setOldPoolLength] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isAlert, setIsAlert] = useState(false);
  const [sendDelete, setSendDelete] = useState(false);
  const [rewardId, setRewardId] = useState('');
  const [expect, setExpect] = useState({
    amt: '0',
    usdt: '0',
  });

  useEffect(() => {
    (async () => {
      const promises = await promiseAllSettled([
        () => readPoolRewardSettingList(pool_id),
        () => readPoolExpectAmtUsdt(pool_id),
      ]);
      // 풀 세팅 목록
      if (promises[0].status !== SUCCESS) {
        return;
      }
      const list: DatePickerType[] = promises[0].data.map((item: ResReadPoolRewardListType, index: number) => ({
        ...item,
        id: index,
        startDt: dayjs(item.startDt),
        endDt: dayjs(item.endDt),
      }));
      setPools(list);
      setOldPoolLength(list.length);

      // 예샹 총 수량 및 금액
      if (promises[1].status !== SUCCESS) {
        return;
      }
      setExpect({ amt: promises[1].data.totExpectAmt, usdt: promises[1].data.totExpectUsdt });
    })();
    return () => {};
  }, [pool_id]);

  useEffect(() => {
    if (sendDelete) {
      (async () => {
        const re = await deleteReward(rewardId);
        if (re.status !== SUCCESS) {
          return;
        }
        setRewardId('');
        setSendDelete(false);
        toast.success('성공');
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      })();
    }

    return () => {};
  }, [sendDelete, rewardId]);

  const handleAddInputs = () => {
    const newId = pools.length + 1;
    setPools([
      ...pools,
      {
        id: newId,
        startDt: null,
        endDt: null,
        poolId: pool_id,
        ticker: '0',
        state: 0,
        totExpectAmt: '0',
        totExpectUsdt: '0',
      },
    ]);
  };
  const handleDelete = () => {
    if (oldPoolLength < pools.length) {
      setPools((prevPools) => prevPools.slice(0, -1));
    }
  };

  const handleOldDelete = (aId: number) => {
    const re = pools.filter((item: DatePickerType) => aId === item.id);
    const { id, ...obj } = re[0];
    // 현재 날짜와 시작 날짜가 같거나 크면 삭제 안되게 변경
    if (RESULT_BIG === compareDate(now(), String(obj.startDt))) {
      toast.error('시작일이 지난 경우 삭제할 수 없습니다.');
      return;
    }
    setRewardId(obj.rewardSetId!);
    setIsAlert(true);
  };

  const handleDateChange = (aId: number, aField: 'startDt' | 'endDt', aValue: Dayjs | null) => {
    setPools((prevPools) => prevPools.map((pool) => (pool.id === aId ? { ...pool, [aField]: aValue } : pool)));
  };

  const handleUpdate = async (aId: number) => {
    setIsLoading(true);

    const re = pools.filter((item: DatePickerType) => aId === item.id);
    const { id, ...obj } = re[0];
    const payload: UpdatePoolRewardType = {
      poolId: pool_id,
      startDt: dayjs(obj.startDt).format(DEFAULT_FORMAT),
      endDt: dayjs(obj.endDt).format(DEFAULT_FORMAT),
      rewardSetId: obj.rewardSetId!,
    };
    try {
      await updatePoolReward(payload);
      toast.success('성공');
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  };
  const handleCreate = async () => {
    // filter 단계에서 poolId가 undefined가 아닌 항목만 남김
    const re: CreatePoolRewardType[] = pools
      .filter(
        (item: DatePickerType) =>
          (item.startDt !== null || item.endDt !== null) && !item?.rewardSetId && item?.poolId !== undefined, // 필터 조건에 추가
      )
      .map((item: DatePickerType) => ({
        poolId: item.poolId!, // poolId가 undefined가 아님이 보장되므로 확실하게 단언
        startDt: dayjs(item.startDt).format(DEFAULT_FORMAT),
        endDt: dayjs(item.endDt).format(DEFAULT_FORMAT),
      }));

    if (re.length === 0) {
      toast.error('추가할 리워드 데이터가 없습니다.');
      return;
    }
    setIsLoading(true);
    try {
      await createPoolReward(re);
      toast.success('성공');
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Box p={2}>
        <Grid container spacing={3} mb={2}>
          <Grid xs={4} sm={2}>
            <Typography variant="h6">시작일</Typography>
          </Grid>
          <Grid xs={3} sm={2}>
            <Typography variant="h6">종료일</Typography>
          </Grid>
          <Grid xs={3} sm={1}>
            <Typography variant="h6">코인 시세</Typography>
          </Grid>
          <Grid xs={3} sm={1}>
            <Typography variant="h6">상태</Typography>
          </Grid>

          <Grid xs={3} sm={2}>
            <Typography variant="h6">예상 수량</Typography>
          </Grid>

          <Grid xs={3} sm={2}>
            <Typography variant="h6">예상 금액</Typography>
          </Grid>
        </Grid>
      </Box>
      <Box p={2}>
        {pools.map((pool: DatePickerType, index: number) => {
          const isOldPool = index < oldPoolLength;

          return (
            <LocalizationProvider dateAdapter={AdapterDayjs} key={pool.id}>
              <Grid container spacing={2}>
                <Grid xs={4} sm={2}>
                  <DatePicker
                    label="시작일"
                    value={pool.startDt}
                    format="YYYY-MM-DD"
                    onChange={(newValue) => handleDateChange(pool.id, 'startDt', newValue)}
                  />
                </Grid>
                <Grid xs={4} sm={2}>
                  <DatePicker
                    label="종료일"
                    value={pool.endDt}
                    format="YYYY-MM-DD"
                    onChange={(newValue) => handleDateChange(pool.id, 'endDt', newValue)}
                  />
                </Grid>

                {isOldPool && (
                  <>
                    <Grid xs={2} sm={1}>
                      ticker: {pool.ticker}
                    </Grid>
                    <Grid xs={2} sm={1}>
                      state: {rewardStateFilter(pool.state)}
                    </Grid>

                    <Grid xs={2} sm={2}>
                      {packBigNumbers(pool.totExpectAmt, 6)}
                    </Grid>

                    <Grid xs={2} sm={2}>
                      {packBigNumbers(pool.totExpectUsdt, 6)}($)
                    </Grid>
                    <Grid xs={2} sm={1}>
                      <Button variant="contained" color="primary" onClick={() => handleUpdate(pool.id)}>
                        업데이트
                      </Button>
                    </Grid>
                    <Grid xs={2} sm={1}>
                      <Button variant="contained" color="primary" onClick={() => handleOldDelete(pool.id)}>
                        삭제
                      </Button>
                    </Grid>
                  </>
                )}
              </Grid>
            </LocalizationProvider>
          );
        })}
        <Grid container spacing={2}>
          <Grid xs={6}>
            <Button fullWidth variant="outlined" color="secondary" onClick={handleAddInputs}>
              추가
            </Button>
          </Grid>
          <Grid xs={6}>
            <Button fullWidth variant="outlined" color="secondary" onClick={handleDelete}>
              삭제
            </Button>
          </Grid>
          <Grid xs={12}>
            <Button fullWidth variant="contained" color="primary" onClick={handleCreate}>
              리워드 생성
            </Button>
          </Grid>
        </Grid>
        <Box mb={3} />
        <Box>예상 총 수량: {packBigNumbers(expect.amt, 6)}</Box>
        <Box>예상 총 금액: {packBigNumbers(expect.usdt, 6)} $</Box>
      </Box>
      <Loading isLoading={isLoading} />
      {isAlert && (
        <SendAlert
          isOpen={isAlert}
          setIsOpen={setIsAlert}
          setIsReturn={setSendDelete}
          title="삭제"
          content="삭제하시겠습니까?"
        />
      )}
    </>
  );
}
